import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export type TeamRole = 'admin' | 'member';
export type TeamPlan = 'free' | 'pro' | 'enterprise';

export interface Team {
  id: string;
  name: string;
  plan: TeamPlan;
  created_at: string;
}

export interface TeamMember {
  user_id: string;
  email: string;
  role: TeamRole;
  joined_at: string;
}

export interface TeamWithMembers extends Team {
  members: TeamMember[];
}

interface TeamsListResponse {
  teams: Team[];
}

interface CreateTeamRequest {
  name: string;
}

export function useTeams() {
  const queryClient = useQueryClient();

  // List user's teams
  const teamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: async () => {
      const response = await apiClient.get<TeamsListResponse>('/api/v1/teams');
      return response.data.teams;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Create a new team
  const createTeamMutation = useMutation({
    mutationFn: async (data: CreateTeamRequest) => {
      const response = await apiClient.post<Team>('/api/v1/teams', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });

  return {
    teams: teamsQuery.data ?? [],
    isLoading: teamsQuery.isLoading,
    error: teamsQuery.error,
    refetch: teamsQuery.refetch,

    createTeam: createTeamMutation.mutateAsync,
    isCreating: createTeamMutation.isPending,
    createError: createTeamMutation.error,
  };
}

interface InviteMemberRequest {
  email: string;
  role: TeamRole;
}

interface UpdateMemberRoleRequest {
  role: TeamRole;
}

export function useTeamMembers(teamId: string | null) {
  const queryClient = useQueryClient();

  const teamQuery = useQuery({
    queryKey: ['team', teamId],
    queryFn: async () => {
      if (!teamId) return null;
      const response = await apiClient.get<TeamWithMembers>(`/api/v1/teams/${teamId}`);
      return response.data;
    },
    enabled: !!teamId,
    staleTime: 1000 * 60,
  });

  const inviteMemberMutation = useMutation({
    mutationFn: async (data: InviteMemberRequest) => {
      if (!teamId) throw new Error('Team ID is required');
      const response = await apiClient.post<{ message: string; member: TeamMember }>(
        `/api/v1/teams/${teamId}/members`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    },
  });

  const updateMemberRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: TeamRole }) => {
      if (!teamId) throw new Error('Team ID is required');
      const response = await apiClient.patch<{ message: string }>(
        `/api/v1/teams/${teamId}/members/${userId}`,
        { role } as UpdateMemberRoleRequest
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!teamId) throw new Error('Team ID is required');
      const response = await apiClient.delete<{ message: string }>(
        `/api/v1/teams/${teamId}/members/${userId}`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    },
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['team', teamId] });
  };

  return {
    team: teamQuery.data,
    members: teamQuery.data?.members ?? [],
    isLoading: teamQuery.isLoading,
    error: teamQuery.error,
    refetch: teamQuery.refetch,
    refresh,

    inviteMember: inviteMemberMutation.mutateAsync,
    isInviting: inviteMemberMutation.isPending,
    inviteError: inviteMemberMutation.error,

    updateMemberRole: updateMemberRoleMutation.mutateAsync,
    isUpdatingRole: updateMemberRoleMutation.isPending,
    updateRoleError: updateMemberRoleMutation.error,

    removeMember: removeMemberMutation.mutateAsync,
    isRemoving: removeMemberMutation.isPending,
    removeError: removeMemberMutation.error,
  };
}
